import React, { useEffect, useState, lazy } from 'react';
import { AlertTriangleIcon, CheckCircleIcon, ShieldCheckIcon, StarIcon, ZapIcon, ArrowRightIcon, BookOpenIcon, UsersIcon, MessageSquareIcon, HeadphonesIcon, DownloadIcon, LockIcon, CreditCardIcon, FlameIcon, BadgeCheckIcon, SparklesIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { AccordionItem } from '../components/ui/Accordion';
import { CountdownTimer } from '../components/CountdownTimer';
import { initMetaPixel, trackPageView, trackAddToCart } from '../utils/metaPixel';
const BOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/wxeAJBDtdTuDfL2YaXhpPe/mock_up.png';
export function SalesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Meta Pixel: init + PageView on load
  useEffect(() => {
    initMetaPixel();
  }, []);
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleCtaClick = () => {
    trackAddToCart(() => {
      window.location.href = 'https://oriammarketing.com/cart/45576443691206:1';
    });
  };
  return <div className="min-h-screen bg-patriot-bg flex flex-col font-body text-gray-300 selection:bg-patriot-red selection:text-white relative" data-id="element-18">
      {/* Background image — subtle, blurred, with dark overlay */}
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-19">
        <img src="https://cdn.magicpatterns.com/uploads/tdFpkc6Wco5hqtwSWF4j1q/heylloyd-image-1772262161275.png" alt="" aria-hidden="true" loading="lazy" className="w-full h-full object-cover opacity-[0.07] blur-[2px]" data-id="element-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-patriot-bg via-patriot-bg/95 to-patriot-bg" data-id="element-21"></div>
      </div>

      {/* 🔴 URGENCY BAR (Sticky Top) */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-patriot-darkRed via-patriot-red to-patriot-darkRed text-white py-2.5 px-2 sm:px-4 shadow-glow-red text-center flex flex-row flex-wrap items-center justify-center gap-1.5 sm:gap-2 border-b border-red-400/30" data-id="element-22">
        <div className="flex items-center font-bold text-[11px] sm:text-sm md:text-base uppercase tracking-wide whitespace-nowrap" data-id="element-23">
          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-white mr-1.5 sm:mr-2 animate-pulse shadow-[0_0_10px_#fff]" data-id="element-24"></div>
          Special Bundle Offer — Limited Time
        </div>
        <CountdownTimer initialMinutes={15} initialSeconds={42} data-id="element-25" />
      </div>

      {/* HERO SECTION */}
      <header className="relative pt-8 pb-14 sm:pt-10 sm:pb-20 md:pt-12 md:pb-28 px-4 overflow-hidden bg-patriot-bgAlt min-h-0 md:min-h-[90vh] flex items-center" data-id="element-26">
        <div className="absolute inset-0 bg-stage-lighting opacity-80 pointer-events-none" data-id="element-27"></div>

        <div className="max-w-7xl mx-auto relative z-10 w-full" data-id="element-28">
          <div className="flex flex-col items-center text-center" data-id="element-29">
            <div className="inline-block bg-patriot-red/10 border border-patriot-red/50 text-patriot-red px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold tracking-widest uppercase mb-5 sm:mb-8 shadow-[0_0_15px_rgba(230,57,70,0.2)]" data-id="element-30">
              Voice Of Freedom
            </div>
            <h1 className="text-[26px] sm:text-[32px] md:text-6xl lg:text-7xl font-bold mb-5 sm:mb-8 text-white drop-shadow-2xl uppercase tracking-tight max-w-5xl" data-id="element-31">
              How to Awaken the Sleeping Giant of Liberty and{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-patriot-red to-red-400 drop-shadow-[0_0_10px_rgba(230,57,70,0.5)]" data-id="element-32">
                Stop Asking for Permission
              </span>
            </h1>

            {/* Book Cover Image with floating badge */}
            <div className="relative mb-6 sm:mb-10" data-id="element-33">
              <div className="absolute inset-0 bg-patriot-red/20 blur-[60px] rounded-full transform scale-150 pointer-events-none" data-id="element-34"></div>
              <div className="absolute inset-0 bg-patriot-blue/15 blur-[60px] rounded-full transform scale-150 translate-x-10 pointer-events-none" data-id="element-35"></div>

              {/* Floating Sales Badge */}
              <div className="absolute -top-4 -right-4 md:-right-8 z-20 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full text-xs md:text-sm font-bold shadow-lg animate-pulse flex items-center gap-1.5" data-id="element-36">
                <FlameIcon className="w-4 h-4" data-id="element-37" />
                2,847 sold this week
              </div>

              <img src={BOOK_COVER_URL} alt="Voice of Freedom: The Legacy of CK" className="relative z-10 w-40 sm:w-52 md:w-64 rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.8),0_0_20px_rgba(230,57,70,0.3)] hover:scale-105 transition-transform duration-500 border border-white/10" data-id="element-38" />
            </div>

            <p className="text-base sm:text-[18px] md:text-2xl text-gray-400 mb-2 sm:mb-4 max-w-3xl font-light" data-id="element-39">
              The Legacy of CK — the full story they never wanted you to read.
            </p>
            <div className="flex items-center justify-center gap-2 mb-6 sm:mb-10 text-xs sm:text-sm md:text-base font-semibold text-gray-300 uppercase tracking-wider" data-id="element-40">
              <HeadphonesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-patriot-gold" data-id="element-41" />
              Audiobook
              <span className="text-patriot-gold" data-id="element-42">+</span>
              <DownloadIcon className="w-4 h-4 sm:w-5 sm:h-5 text-patriot-gold" data-id="element-43" />
              Instant Download E-book
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-44">
              <Button variant="gold" size="xl" onClick={scrollToOffer} className="animate-pulse-glow group w-full sm:w-auto" data-id="element-45">
                Yes, I Want To Reclaim My Voice
                <ArrowRightIcon className="ml-2 w-5 h-5 sm:ml-3 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" data-id="element-46" />
              </Button>
            </div>
            <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500 flex items-center justify-center font-medium tracking-wide" data-id="element-47">
              <ShieldCheckIcon className="w-4 h-4 mr-2 text-patriot-gold" data-id="element-48" />{' '}
              100% Secure Checkout • 7-Day Guarantee
            </p>

            {/* As Featured In Bar */}
            <div className="mt-8 sm:mt-16 pt-6 sm:pt-10 border-t border-gray-800/50 w-full max-w-3xl" data-id="element-49">
              <p className="text-xs text-gray-600 uppercase tracking-widest mb-4 sm:mb-6 font-semibold" data-id="element-50">
                As Featured In
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 md:gap-8" data-id="element-51">
                <span className="text-gray-500 font-bold text-xs sm:text-sm md:text-base tracking-wider px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-800/30 rounded-full" data-id="element-52">
                  FOX NEWS
                </span>
                <span className="text-gray-500 font-bold text-xs sm:text-sm md:text-base tracking-wider px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-800/30 rounded-full" data-id="element-53">
                  NEWSMAX
                </span>
                <span className="text-gray-500 font-bold text-xs sm:text-sm md:text-base tracking-wider px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-800/30 rounded-full" data-id="element-54">
                  THE DAILY WIRE
                </span>
                <span className="text-gray-500 font-bold text-xs sm:text-sm md:text-base tracking-wider px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-800/30 rounded-full" data-id="element-55">
                  PRAGERU
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="h-1 w-full bg-gradient-divider opacity-60" data-id="element-56"></div>

      {/* SECTION 1: THE HOOK */}
      <section className="py-12 sm:py-16 md:py-28 px-4 bg-patriot-bg relative overflow-hidden" data-id="element-57">
        <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-58">
          <img src="https://cdn.magicpatterns.com/uploads/aN7WhmJjrMmN3jQALSby5a/heylloyd-image-1772262161275.png" alt="" aria-hidden="true" loading="lazy" className="w-full h-full object-cover opacity-50" data-id="element-59" />
          <div className="absolute inset-0 bg-gradient-to-b from-patriot-bg/60 via-transparent to-patriot-bg/60" data-id="element-60"></div>
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-patriot-red/5 blur-[120px] pointer-events-none" data-id="element-61"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-62">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-patriot-red/10 border border-patriot-red/30 mb-5 sm:mb-8 shadow-glow-red" data-id="element-63">
            <ZapIcon className="w-7 h-7 sm:w-10 sm:h-10 text-patriot-red" data-id="element-64" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-5 sm:mb-8 uppercase tracking-tight" data-id="element-65">
            Have you ever felt the need to lower your voice when talking about
            your values?
          </h2>
          <p className="text-base sm:text-xl md:text-2xl text-gray-400 leading-relaxed mb-6 sm:mb-10 font-light" data-id="element-66">
            Whether at work, on campus, or even on social media... that
            tightening in your chest isn't shyness. It's the weight of a system
            designed to crush your spirit into conformity.
          </p>
          <div className="border-l-4 border-patriot-red pl-5 sm:pl-8 py-4 sm:py-6 text-left bg-gradient-to-r from-patriot-red/10 to-transparent rounded-r-2xl relative" data-id="element-67">
            <div className="absolute left-0 top-0 w-1 h-full bg-patriot-red shadow-[0_0_15px_#e63946] rounded-full" data-id="element-68"></div>
            <p className="text-base sm:text-xl md:text-2xl text-white leading-relaxed font-semibold italic" data-id="element-69">
              Charlie Kirk knew that weight—and he decided his microphone would
              be louder than his fear.
            </p>
          </div>
        </div>
      </section>

      <div className="h-1 w-full bg-gradient-divider opacity-60" data-id="element-70"></div>

      {/* SECTION 2: AGITATING THE PAIN */}
      <section className="py-12 sm:py-16 md:py-28 px-4 bg-patriot-bgAlt relative" data-id="element-71">
        <div className="max-w-5xl mx-auto" data-id="element-72">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center" data-id="element-73">
            <div className="order-2 md:order-1 relative" data-id="element-74">
              <div className="absolute inset-0 bg-patriot-blue/20 blur-[80px] rounded-full pointer-events-none" data-id="element-75"></div>
              <div className="relative z-10 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-800 shadow-2xl" data-id="element-76">
                <div className="absolute inset-0 bg-gradient-to-t from-patriot-bg to-transparent z-10" data-id="element-77"></div>
                <img src="https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?q=80&w=2070&auto=format&fit=crop" alt="Crowd of people" loading="lazy" className="w-full h-full object-cover grayscale contrast-125 opacity-60" data-id="element-78" />
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 z-20" data-id="element-79">
                  <p className="text-patriot-red font-bold text-base sm:text-xl uppercase tracking-widest drop-shadow-md" data-id="element-80">
                    The Silent Majority
                  </p>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2" data-id="element-81">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-5 sm:mb-8 uppercase tracking-tight leading-tight" data-id="element-82">
                You live in a world where common sense is treated like a hate
                crime.
              </h2>
              <div className="space-y-4 sm:space-y-6 text-base sm:text-xl text-gray-400 font-light" data-id="element-83">
                <p data-id="element-84">
                  Where universities—once the cradles of ideas—have become
                  factories of compliance.
                </p>
                <p data-id="element-85">
                  You feel isolated, surrounded by a crowd that nods only out of
                  fear of social exile or losing their career.
                </p>
                <p className="font-bold text-patriot-red text-lg sm:text-2xl drop-shadow-[0_0_10px_rgba(230,57,70,0.3)]" data-id="element-86">
                  The "shame" they try to impose on you is their greatest
                  weapon.
                </p>
                <div className="pt-4 sm:pt-6 mt-4 sm:mt-6 border-t border-gray-800" data-id="element-87">
                  <p className="text-lg sm:text-2xl font-heading font-semibold text-white uppercase tracking-wide" data-id="element-88">
                    Are you going to let them edit your reality until there is
                    nothing left of the real you?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-1 w-full bg-gradient-divider opacity-60" data-id="element-89"></div>

      {/* SECTION 3: THE REVELATION */}
      <section className="py-14 sm:py-20 md:py-32 px-4 bg-patriot-darkBlue relative overflow-hidden" data-id="element-90">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1460467820054-c87ab43e9b59?q=80&w=2067&auto=format&fit=crop')] bg-cover bg-center opacity-5 mix-blend-luminosity pointer-events-none" data-id="element-91"></div>
        <div className="absolute inset-0 bg-stage-lighting-center opacity-60 pointer-events-none" data-id="element-92"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-93">
          <h2 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-6 sm:mb-10 text-white uppercase tracking-tight drop-shadow-lg" data-id="element-94">
            Charlie's "Secret" wasn't an Ivy League degree or political
            connections.
          </h2>
          <div className="inline-block bg-gradient-to-r from-patriot-gold to-patriot-darkGold text-white text-xl sm:text-3xl md:text-5xl font-black px-6 sm:px-10 py-4 sm:py-6 rounded-xl sm:rounded-2xl shadow-glow-gold mb-6 sm:mb-12 transform -rotate-2 border border-yellow-300/50 uppercase tracking-widest" data-id="element-95">
            It was Sovereignty of Truth.
          </div>
          <p className="text-base sm:text-xl md:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto font-light" data-id="element-96">
            This book reveals why everything they've taught you about "progress"
            is actually a golden cage—and how you can break it using the exact
            tools Charlie used to build Turning Point USA from scratch:{' '}
            <strong className="text-white font-bold block mt-3 sm:mt-4 text-lg sm:text-2xl drop-shadow-md" data-id="element-97">
              Conviction, Faith, and Impactful Communication.
            </strong>
          </p>
        </div>
      </section>

      <div className="h-1 w-full bg-gradient-divider opacity-60" data-id="element-98"></div>

      {/* SECTION 4: SOCIAL PROOF */}
      <section className="py-12 sm:py-16 md:py-28 px-4 bg-patriot-bg relative" data-id="element-99">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-full bg-patriot-blue/5 blur-[150px] pointer-events-none" data-id="element-100"></div>
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-101">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-center text-white mb-6 sm:mb-10 uppercase tracking-tight" data-id="element-102">
            Join Thousands Who Have Already{' '}
            <span className="text-patriot-red" data-id="element-103">Reclaimed Their Voice</span>
          </h2>

          {/* Trustpilot-style Aggregate Rating */}
          <div className="flex flex-col items-center mb-8 sm:mb-14" data-id="element-104">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3" data-id="element-105">
              <span className="text-white font-bold text-sm sm:text-lg" data-id="element-106">
                Excellent
              </span>
              <div className="flex gap-0.5" data-id="element-107">
                {[...Array(5)].map((_, i) => <div key={i} className="w-5 h-5 sm:w-7 sm:h-7 bg-[#00b67a] flex items-center justify-center" data-id="element-108">
                    <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white fill-white" data-id="element-109" />
                  </div>)}
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm" data-id="element-110">
              Based on{' '}
              <span className="text-white font-semibold underline" data-id="element-111">
                2,847 reviews
              </span>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-112">
            {/* Review 1 */}
            <div className="bg-patriot-card/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 hover:border-gray-700 transition-all duration-300 flex flex-col h-full" data-id="element-113">
              <div className="flex gap-0.5 mb-3" data-id="element-114">
                {[...Array(5)].map((_, i) => <div key={i} className="w-6 h-6 bg-[#00b67a] flex items-center justify-center" data-id="element-115">
                    <StarIcon className="w-3.5 h-3.5 text-white fill-white" data-id="element-116" />
                  </div>)}
              </div>
              <p className="text-white font-bold text-base mb-2" data-id="element-117">
                Changed my entire perspective
              </p>
              <p className="text-gray-400 text-sm leading-relaxed flex-grow mb-5" data-id="element-118">
                "I went from staying silent in my Poly-Sci classes to leading
                the debate. In 30 days, my confidence is unbreakable. 'Voices of
                Freedom' gave me the vocabulary I was missing."
              </p>
              <div className="flex items-center justify-between border-t border-gray-800 pt-4" data-id="element-119">
                <div className="flex items-center gap-2.5" data-id="element-120">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-patriot-red to-red-700 flex items-center justify-center text-white font-bold text-xs" data-id="element-121">
                    JD
                  </div>
                  <div data-id="element-122">
                    <span className="text-white font-semibold text-sm block" data-id="element-123">
                      John D.
                    </span>
                    <span className="text-gray-500 text-xs" data-id="element-124">Texas, US</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[#00b67a] text-xs font-medium" data-id="element-125">
                  <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-126" />
                  Verified
                </div>
              </div>
            </div>

            {/* Review 2 */}
            <div className="bg-patriot-card/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 hover:border-gray-700 transition-all duration-300 flex flex-col h-full" data-id="element-127">
              <div className="flex gap-0.5 mb-3" data-id="element-128">
                {[...Array(5)].map((_, i) => <div key={i} className="w-6 h-6 bg-[#00b67a] flex items-center justify-center" data-id="element-129">
                    <StarIcon className="w-3.5 h-3.5 text-white fill-white" data-id="element-130" />
                  </div>)}
              </div>
              <p className="text-white font-bold text-base mb-2" data-id="element-131">
                A must-read for every American family
              </p>
              <p className="text-gray-400 text-sm leading-relaxed flex-grow mb-5" data-id="element-132">
                "As a mother, this book helped me explain to my kids why the
                American Dream is worth fighting for. We went from worrying to
                taking family action."
              </p>
              <div className="flex items-center justify-between border-t border-gray-800 pt-4" data-id="element-133">
                <div className="flex items-center gap-2.5" data-id="element-134">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-patriot-gold to-yellow-600 flex items-center justify-center text-white font-bold text-xs" data-id="element-135">
                    SM
                  </div>
                  <div data-id="element-136">
                    <span className="text-white font-semibold text-sm block" data-id="element-137">
                      Sarah M.
                    </span>
                    <span className="text-gray-500 text-xs" data-id="element-138">Florida, US</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[#00b67a] text-xs font-medium" data-id="element-139">
                  <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-140" />
                  Verified
                </div>
              </div>
            </div>

            {/* Review 3 */}
            <div className="bg-patriot-card/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 hover:border-gray-700 transition-all duration-300 flex flex-col h-full" data-id="element-141">
              <div className="flex gap-0.5 mb-3" data-id="element-142">
                {[...Array(5)].map((_, i) => <div key={i} className="w-6 h-6 bg-[#00b67a] flex items-center justify-center" data-id="element-143">
                    <StarIcon className="w-3.5 h-3.5 text-white fill-white" data-id="element-144" />
                  </div>)}
              </div>
              <p className="text-white font-bold text-base mb-2" data-id="element-145">
                Faith meets the public square
              </p>
              <p className="text-gray-400 text-sm leading-relaxed flex-grow mb-5" data-id="element-146">
                "A necessary bridge between faith and the public square. My
                youth group now sees their faith not as a relic, but as a
                shield."
              </p>
              <div className="flex items-center justify-between border-t border-gray-800 pt-4" data-id="element-147">
                <div className="flex items-center gap-2.5" data-id="element-148">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-patriot-blue to-blue-700 flex items-center justify-center text-white font-bold text-xs" data-id="element-149">
                    PR
                  </div>
                  <div data-id="element-150">
                    <span className="text-white font-semibold text-sm block" data-id="element-151">
                      Pastor Ricardo
                    </span>
                    <span className="text-gray-500 text-xs" data-id="element-152">Arizona, US</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[#00b67a] text-xs font-medium" data-id="element-153">
                  <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-154" />
                  Verified
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-1 w-full bg-gradient-divider opacity-60" data-id="element-155"></div>

      {/* SECTION 5 & 6: THE OFFER STACK & PRICE */}
      <section id="offer-section" className="py-12 sm:py-16 md:py-28 px-4 bg-patriot-bgAlt relative overflow-hidden" data-id="element-156">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-patriot-red via-patriot-gold to-patriot-blue" data-id="element-157"></div>
        <div className="absolute inset-0 bg-stage-lighting opacity-40 pointer-events-none" data-id="element-158"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-159">
          <div className="text-center mb-8 sm:mb-16" data-id="element-160">
            <h2 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-2 sm:mb-4 text-white uppercase tracking-tight drop-shadow-lg" data-id="element-161">
              THE ULTIMATE LEGACY BUNDLE
            </h2>
            <p className="text-sm sm:text-xl text-patriot-gold font-medium tracking-wide uppercase" data-id="element-162">
              Everything you need to dominate the cultural battlefield.
            </p>
          </div>

          <div className="bg-patriot-card border border-gray-700 rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative" data-id="element-163">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" data-id="element-164"></div>

            <div className="grid lg:grid-cols-5 gap-0" data-id="element-165">
              {/* Left: Book Image (Desktop) */}
              <div className="hidden lg:flex col-span-2 bg-patriot-bg/50 p-12 items-center justify-center border-r border-gray-800 relative" data-id="element-166">
                <div className="absolute inset-0 bg-patriot-red/10 blur-[50px] rounded-full pointer-events-none" data-id="element-167"></div>
                <img src={BOOK_COVER_URL} alt="Voice of Freedom Bundle" loading="lazy" className="w-full max-w-[300px] rounded-2xl shadow-[0_0_30px_rgba(0,0,0,0.8)] relative z-10 transform -rotate-3" data-id="element-168" />
              </div>

              {/* Right: Stack Details */}
              <div className="col-span-3 p-5 sm:p-6 md:p-12" data-id="element-169">
                {/* Mobile Book Image */}
                <div className="lg:hidden flex justify-center mb-6 relative" data-id="element-170">
                  <div className="absolute inset-0 bg-patriot-red/10 blur-[30px] rounded-full pointer-events-none" data-id="element-171"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/pbMdKGPXQPaim4UcdnDQtb/ChatGPT_Image_28_feb_2026,_12_53_34_a.m..png" alt="Voice of Freedom Bundle — Audiobook + E-book" loading="lazy" className="w-44 sm:w-56 md:w-64 rounded-2xl shadow-2xl relative z-10" data-id="element-172" />
                </div>

                {/* Value Stack */}
                <div className="space-y-2.5 sm:space-y-4 mb-6 sm:mb-10" data-id="element-173">
                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-patriot-bg/50 rounded-xl sm:rounded-2xl border border-gray-800" data-id="element-174">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-patriot-red/20 flex items-center justify-center flex-shrink-0" data-id="element-175">
                      <BookOpenIcon className="w-4 h-4 sm:w-6 sm:h-6 text-patriot-red" data-id="element-176" />
                    </div>
                    <div className="flex-grow min-w-0" data-id="element-177">
                      <span className="text-white font-bold text-sm sm:text-lg block" data-id="element-178">
                        Complete E-book
                      </span>
                      <span className="text-gray-500 text-xs sm:text-sm block" data-id="element-179">
                        Instant digital download
                      </span>
                    </div>
                    <span className="text-gray-400 font-semibold text-xs sm:text-base whitespace-nowrap" data-id="element-180">
                      $47
                    </span>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-patriot-bg/50 rounded-xl sm:rounded-2xl border border-gray-800" data-id="element-181">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-patriot-gold/20 flex items-center justify-center flex-shrink-0" data-id="element-182">
                      <HeadphonesIcon className="w-4 h-4 sm:w-6 sm:h-6 text-patriot-gold" data-id="element-183" />
                    </div>
                    <div className="flex-grow min-w-0" data-id="element-184">
                      <span className="text-white font-bold text-sm sm:text-lg block" data-id="element-185">
                        Full Audiobook
                      </span>
                      <span className="text-gray-500 text-xs sm:text-sm block" data-id="element-186">
                        Listen anywhere, anytime
                      </span>
                    </div>
                    <span className="text-gray-400 font-semibold text-xs sm:text-base whitespace-nowrap" data-id="element-187">
                      $37
                    </span>
                  </div>
                </div>

                {/* Total Value */}
                <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-800/50 rounded-xl sm:rounded-2xl mb-5 sm:mb-8 border border-gray-700" data-id="element-188">
                  <span className="text-gray-400 font-semibold uppercase tracking-wider text-xs sm:text-base" data-id="element-189">
                    Total Value:
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-white" data-id="element-190">
                    $84
                  </span>
                </div>

                <div className="border-t border-gray-700 pt-6 sm:pt-10 text-center relative" data-id="element-191">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-patriot-card px-3 text-gray-500 text-xs sm:text-sm uppercase tracking-widest font-bold" data-id="element-192">
                    Your Price Today
                  </div>

                  {/* Price with Value Anchoring */}
                  <div className="mb-3 sm:mb-6" data-id="element-193">
                    <span className="text-xl sm:text-3xl text-gray-500 line-through mr-2 sm:mr-4" data-id="element-194">
                      $84
                    </span>
                    <span className="text-5xl sm:text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-patriot-gold to-yellow-400 drop-shadow-lg tracking-tighter" data-id="element-195">
                      $14.99
                    </span>
                  </div>

                  {/* Savings Badge */}
                  <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-green-500/20 border border-green-500/50 text-green-400 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold mb-4 sm:mb-8" data-id="element-196">
                    <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-197" />
                    You Save $69 (82% OFF)
                  </div>

                  <p className="text-gray-400 font-medium mb-4 sm:mb-8 uppercase tracking-widest text-[10px] sm:text-sm" data-id="element-198">
                    One-time payment. Instant digital access.
                  </p>

                  {/* Join Patriots Text */}
                  <p className="text-patriot-gold font-semibold mb-4 sm:mb-6 flex items-center justify-center gap-2 text-xs sm:text-base" data-id="element-199">
                    <UsersIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-200" />
                    Join 12,000+ patriots who've reclaimed their voice
                  </p>

                  <Button variant="gold" size="xl" fullWidth onClick={handleCtaClick} className="text-sm sm:text-base md:text-xl lg:text-2xl py-3.5 sm:py-4 md:py-6 shadow-glow-gold animate-pulse-glow" data-id="element-201">
                    YES, I WANT TO RECLAIM MY VOICE — $14.97
                  </Button>

                  {/* Trust Icons */}
                  <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-5 sm:mt-8 text-gray-500 text-[10px] sm:text-sm" data-id="element-202">
                    <span className="flex items-center gap-1 sm:gap-2" data-id="element-203">
                      <LockIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" data-id="element-204" />
                      Secure Checkout
                    </span>
                    <span className="flex items-center gap-1 sm:gap-2" data-id="element-205">
                      <CreditCardIcon className="w-3 h-3 sm:w-4 sm:h-4 text-patriot-blue" data-id="element-206" />
                      All Cards Accepted
                    </span>
                    <span className="flex items-center gap-1 sm:gap-2" data-id="element-207">
                      <ZapIcon className="w-3 h-3 sm:w-4 sm:h-4 text-patriot-gold" data-id="element-208" />
                      Instant Access
                    </span>
                  </div>

                  <p className="text-sm sm:text-lg text-gray-400 mt-6 sm:mt-10 italic font-light border-l-2 border-patriot-gold pl-3 sm:pl-4 text-left rounded-r-xl" data-id="element-209">
                    "Why this price? Because the message is more important than
                    the margin. We need 1,000,000 voices, not 1,000 elites."
                  </p>

                  <div className="flex items-center justify-center gap-2 mt-5 sm:mt-8 opacity-40 hover:opacity-60 transition-opacity" data-id="element-210">
                    <LockIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-211" />
                    <span className="text-gray-400 text-xs sm:text-sm font-medium tracking-wide" data-id="element-212">
                      Powered by
                    </span>
                    <span className="text-gray-300 text-sm sm:text-base font-bold tracking-wider" data-id="element-213">
                      stripe
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-1 w-full bg-gradient-divider opacity-60" data-id="element-214"></div>

      {/* SECTION 7: FAQ */}
      <section className="py-12 sm:py-16 md:py-28 px-4 bg-patriot-bg relative" data-id="element-215">
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-216">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-center text-white mb-8 sm:mb-16 uppercase tracking-tight" data-id="element-217">
            Frequently Asked Questions
          </h2>
          <div className="bg-patriot-card rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 md:p-10 border border-gray-800" data-id="element-218">
            <AccordionItem question="Is this only for politicians?" answer="No. This is for any citizen, student, or parent who feels their personal freedom is being eroded and wants practical tools to push back." isOpen={openFaq === 0} onClick={() => setOpenFaq(openFaq === 0 ? null : 0)} data-id="element-219" />
            <AccordionItem question="Is the content too dense or academic?" answer="Absolutely not. Charlie spoke so everyone could understand. This book is written in 'Lightning Bolts'—short, punchy, and actionable today." isOpen={openFaq === 1} onClick={() => setOpenFaq(openFaq === 1 ? null : 1)} data-id="element-220" />
            <AccordionItem question="What if I don't agree with 100% of his views?" answer="The book isn't designed to make you a clone; it's designed to give you the courage to think for yourself and defend your truth." isOpen={openFaq === 2} onClick={() => setOpenFaq(openFaq === 2 ? null : 2)} data-id="element-221" />
          </div>
        </div>
      </section>

      <div className="h-1 w-full bg-gradient-divider opacity-60" data-id="element-222"></div>

      {/* SECTION 8: THE GUARANTEE */}
      <section className="py-12 sm:py-16 md:py-28 px-4 bg-patriot-bgAlt relative overflow-hidden" data-id="element-223">
        <div className="absolute inset-0 bg-patriot-blue/5 blur-[100px] pointer-events-none" data-id="element-224"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-225">
          {/* Visual Seal/Badge */}
          <div className="inline-block relative mb-6 sm:mb-10" data-id="element-226">
            <div className="absolute inset-0 bg-patriot-gold/30 blur-[40px] rounded-full scale-150" data-id="element-227"></div>
            <div className="relative w-20 h-20 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-patriot-gold via-yellow-500 to-patriot-darkGold p-1 shadow-glow-gold" data-id="element-228">
              <div className="w-full h-full rounded-full bg-patriot-bgAlt flex items-center justify-center border-2 sm:border-4 border-dashed border-patriot-gold/50" data-id="element-229">
                <ShieldCheckIcon className="w-8 h-8 sm:w-14 sm:h-14 text-patriot-gold drop-shadow-[0_0_10px_rgba(245,158,11,0.5)]" data-id="element-230" />
              </div>
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-patriot-gold text-patriot-bg text-[10px] sm:text-xs font-black px-3 sm:px-4 py-1 rounded-full uppercase tracking-widest shadow-lg" data-id="element-231">
              Guaranteed
            </div>
          </div>

          <h2 className="text-2xl sm:text-4xl md:text-6xl font-bold text-white mb-5 sm:mb-8 uppercase tracking-tight drop-shadow-lg" data-id="element-232">
            THE ZERO-RISK CHALLENGE
          </h2>
          <div className="bg-patriot-card/50 backdrop-blur-md border-2 border-patriot-blue/30 rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-12 shadow-glow-blue relative overflow-hidden" data-id="element-233">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-patriot-blue to-transparent" data-id="element-234"></div>
            <p className="text-base sm:text-xl md:text-2xl text-gray-300 leading-relaxed font-light" data-id="element-235">
              If within 7 days you don't feel more clarity and strength to
              defend your ideas, I will refund every single penny.{' '}
              <strong className="text-white font-semibold block mt-3 sm:mt-4 text-lg sm:text-2xl" data-id="element-236">
                No questions asked. And you keep the book.
              </strong>
            </p>
            <p className="text-base sm:text-xl text-patriot-gold mt-5 sm:mt-8 font-bold uppercase tracking-widest drop-shadow-md" data-id="element-237">
              My mission is your freedom, not your money.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 9: HIGH-CONVERSION CTA */}
      <section className="py-14 sm:py-20 md:py-32 px-4 bg-patriot-bg text-center relative overflow-hidden border-t border-gray-800" data-id="element-238">
        <div className="absolute inset-0 bg-stage-lighting opacity-80 pointer-events-none" data-id="element-239"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-1/2 bg-patriot-red/10 blur-[100px] pointer-events-none" data-id="element-240"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-241">
          <img src={BOOK_COVER_URL} alt="Voice of Freedom" loading="lazy" className="w-32 sm:w-48 mx-auto mb-6 sm:mb-12 rounded-2xl shadow-[0_0_40px_rgba(230,57,70,0.4)] transform -rotate-2" data-id="element-242" />
          <h2 className="text-3xl sm:text-5xl md:text-7xl font-black mb-5 sm:mb-8 leading-tight text-white uppercase tracking-tighter drop-shadow-2xl" data-id="element-243">
            Don't let another day go by where you feel silenced.
          </h2>
          <p className="text-lg sm:text-2xl md:text-3xl text-gray-400 mb-5 sm:mb-8 font-light" data-id="element-244">
            The legacy of freedom isn't inherited—
            <strong className="text-white font-bold block mt-2" data-id="element-245">
              it's fought for. Join the movement.
            </strong>
          </p>

          {/* Urgency Text */}
          <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-patriot-red/20 border border-patriot-red/50 text-patriot-red px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm md:text-base font-bold mb-6 sm:mb-10 animate-pulse" data-id="element-246">
            <AlertTriangleIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-247" />
            Price increases to $47 when timer hits zero
          </div>

          <Button variant="gold" size="xl" onClick={scrollToOffer} className="text-base sm:text-lg md:text-xl lg:text-3xl py-4 sm:py-5 md:py-6 px-8 sm:px-10 md:px-12 shadow-glow-gold-lg animate-pulse-glow uppercase tracking-widest w-full sm:w-auto" data-id="element-248">
            YES, I WANT TO RECLAIM MY VOICE
          </Button>

          {/* Benefit Icons */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 mt-6 sm:mt-10 text-gray-400 text-xs sm:text-sm" data-id="element-249">
            <div className="flex flex-col items-center gap-1.5 sm:gap-2" data-id="element-250">
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-patriot-gold/10 flex items-center justify-center" data-id="element-251">
                <ZapIcon className="w-4 h-4 sm:w-6 sm:h-6 text-patriot-gold" data-id="element-252" />
              </div>
              <span data-id="element-253">Instant Access</span>
            </div>
            <div className="flex flex-col items-center gap-1.5 sm:gap-2" data-id="element-254">
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-green-500/10 flex items-center justify-center" data-id="element-255">
                <ShieldCheckIcon className="w-4 h-4 sm:w-6 sm:h-6 text-green-500" data-id="element-256" />
              </div>
              <span data-id="element-257">7-Day Guarantee</span>
            </div>
            <div className="flex flex-col items-center gap-1.5 sm:gap-2" data-id="element-258">
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-patriot-blue/10 flex items-center justify-center" data-id="element-259">
                <LockIcon className="w-4 h-4 sm:w-6 sm:h-6 text-patriot-blue" data-id="element-260" />
              </div>
              <span data-id="element-261">Secure Payment</span>
            </div>
          </div>

          <p className="mt-6 sm:mt-10 text-gray-500 font-bold uppercase tracking-widest text-xs sm:text-sm" data-id="element-262">
            Instant Access • $14.97 One-Time Payment
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050508] text-gray-600 py-8 sm:py-12 text-center text-sm border-t border-gray-900 relative z-20" data-id="element-263">
        <div className="max-w-4xl mx-auto px-4" data-id="element-264">
          <p className="uppercase tracking-widest font-bold mb-4 sm:mb-6 text-xs sm:text-sm" data-id="element-265">
            &copy; {new Date().getFullYear()} Voices of Freedom. All rights
            reserved.
          </p>
          <div className="flex justify-center gap-4 sm:gap-8 mt-3 sm:mt-4" data-id="element-266">
            <a href="#" className="hover:text-white transition-colors uppercase tracking-wider text-xs" data-id="element-267">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white transition-colors uppercase tracking-wider text-xs" data-id="element-268">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors uppercase tracking-wider text-xs" data-id="element-269">
              Contact Support
            </a>
          </div>
        </div>
      </footer>

      {/* MOBILE STICKY CTA BAR */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-patriot-bg/95 backdrop-blur-md border-t border-gray-800 p-4 transition-all duration-300 ${showStickyBar ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`} data-id="element-270">
        <div className="flex items-center justify-between gap-4 max-w-lg mx-auto" data-id="element-271">
          <div className="flex-shrink-0" data-id="element-272">
            <span className="text-gray-400 text-xs line-through" data-id="element-273">$84</span>
            <span className="text-white font-black text-2xl ml-2" data-id="element-274">$14.99</span>
          </div>
          <Button variant="gold" size="lg" onClick={scrollToOffer} className="flex-grow shadow-glow-gold" data-id="element-275">
            GET INSTANT ACCESS
          </Button>
        </div>
      </div>
    </div>;
}